import { Duplexer } from '@wix/duplexer-js';
import { DuplexerEvent, DuplexerSubscriber } from '@wix/wix-help-widget-common/duplexer';
import { Store } from 'redux';

import { IInitialChatData, TUi } from '../../../../server/service/InitialChatData';
import { TRootState } from '../../../common/reducers';
import { chatActions } from '../../../common/reducers/chat';
import { widgetSessionActions } from '../../../common/reducers/widgetSession';
import { selectWidgetSessionTabId } from '../../../common/selectors/widgetSession';
import { createAfter, createBefore, createMessages, TMessage } from '../../../messageChannel/createQueue';
import { Queue } from '../../../messageChannel/Queue';
import {
  getAction,
  isNewUrlChangedDuplexerEvent,
  isUrlChangedDuplexerEvent,
} from '../../../messageChannel/utils';
import { emitter, LiveChatFeedbackSubmittedPayload } from '../../../util/emitter';
import { messagesPolling } from '../../../util/live-chat-polling';
import { EventSource, logger } from '../../../util/logger';
import { renderModel } from '../../../util/renderModel';
import { markAsRead } from '../../api/messageApi';

const isCopilot = renderModel.isCopilot();

export class ChatbotMessageSubscriberWithRead extends DuplexerSubscriber {
  private readonly queue: Queue;
  private _forceIgnoreMessages: boolean = false;
  private handledResponseIdsMap: Map<string, boolean> = new Map();

  constructor(
    protected readonly duplexer: Duplexer,
    protected readonly appDefId: string,
    private readonly store: Store<TRootState>,
  ) {
    super(duplexer, appDefId, logger);

    this.queue = new Queue(store.dispatch);
    emitter.on('chatbotStateChanged', this.onChatbotStateChanged(EventSource.EMITTER));
    // TODO: this event will be part of the WidgetSessionChannel
    this.subscribe(DuplexerEvent.URL_CHANGED, this.onUrlChanged);
    this.subscribe(DuplexerEvent.CHATBOT_STATE_UPDATE, this.onChatbotStateChanged(EventSource.DUPLEXER));
    this.subscribe(DuplexerEvent.LIVE_CHAT_FEEDBACK_SUBMITTED, this.onLiveChatFeedbackSubmitted);
  }

  unsubscribe() {
    super.unsubscribe();
    emitter.remove('chatbotStateChanged');
  }

  set forceIgnoreMessages(value: boolean) {
    this._forceIgnoreMessages = value;
  }

  private shouldIgnoreMessages(
    ignored: boolean,
    notHandledMessages: string[],
    responseId: string,
    ui?: TUi,
  ): boolean {
    if (this.handledResponseIdsMap.has(responseId)) {
      return true;
    }
    return (
      this._forceIgnoreMessages || ignored || (!notHandledMessages.length && !!ui && !Object.keys(ui).length)
    );
  }

  /**
   * Receiving a message from chatbot to user.
   * Process it in our queue and post message to widget
   * @param source
   * @param data
   *
   */
  private onChatbotStateChanged = (source: EventSource) => {
    return (data: IInitialChatData): void => {
      const {
        id: responseId = '',
        conversationId,
        messages,
        ui,
        startChatData,
        meta,
        ignored,
        statusBanner,
        unreadMessagesCount,
      } = data;

      logger.logChatbotStateChanged('Chatbot state changed event received', source, data);

      const localConversationId = this.store.getState().chat.conversationId;
      const eventConversationId = startChatData?.conversationId;
      if (
        source === EventSource.DUPLEXER &&
        localConversationId &&
        eventConversationId &&
        eventConversationId !== localConversationId
      ) {
        return;
      }

      const notHandledMessages = messages?.filter(
        (message) => message && !this.queue.hasHandled(message.id),
      )!;

      if (this.shouldIgnoreMessages(!!ignored, notHandledMessages, responseId, ui)) {
        return;
      }
      if (responseId) {
        this.handledResponseIdsMap.set(responseId, true);
      }

      const notHandledMessageIds = notHandledMessages.map(({ id }) => id);

      logger.logChatbotStateChanged('Chatbot messages to handle', source, {
        ...data,
        messages: notHandledMessageIds,
      });

      const lastMessage = String(notHandledMessageIds.slice(-1));

      if (lastMessage) {
        messagesPolling.updateLastMessage(lastMessage);
      }

      const newMessages: TMessage[] = createMessages(notHandledMessages, this.store);
      this.queue.addActions({
        notHandledMessageIds,
        before: createBefore(startChatData),
        after: createAfter(ui, this.store, meta, statusBanner),
        messages: newMessages,
      });
      this.store.dispatch(chatActions.updateUnreadCount(unreadMessagesCount));
      const isCopilotChatbotVisible = this.store.getState().meta.isCopilotChatbotVisible;
      if (isCopilot && !isCopilotChatbotVisible) {
        return;
      }
      markAsRead(conversationId, lastMessage);
    };
  };

  private onLiveChatFeedbackSubmitted = (data: LiveChatFeedbackSubmittedPayload): void => {
    emitter.emit('liveChatFeedbackSubmitted', data);
  };

  // TODO: move it to widget session channel (not now)
  /**
   * On navigation history (widget session) change, we update all tabs.
   * @param data
   */
  private onUrlChanged = (data: unknown): void => {
    if (!isUrlChangedDuplexerEvent(data) && !isNewUrlChangedDuplexerEvent(data)) {
      console.error(`Payload of DuplexerEvent.URL_CHANGED is not valid: ${JSON.stringify(data)}`);
      return;
    }
    const sessionTabId = selectWidgetSessionTabId(this.store.getState());
    const action = getAction(data);
    if (action.tabId !== sessionTabId) {
      this.store.dispatch(widgetSessionActions.urlChanged(action));
    }
  };
}
